export const locales = ["en", "hi"] as const;
export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = "en";

/** URL prefix for each locale. Default locale has no prefix (root). */
export const localePrefix: Record<Locale, string> = {
  en: "",
  hi: "/hi",
};

/** BCP 47 language tag for the HTML lang attribute. */
export const localeLang: Record<Locale, string> = {
  en: "en",
  hi: "hi",
};

/** Display name shown in the language toggle UI. */
export const localeLabel: Record<Locale, string> = {
  en: "EN",
  hi: "हिंदी",
};

/**
 * Slugs whose translated page intentionally uses a different URL slug.
 * Keeps language toggle and hreflang alternates from linking to 404s.
 */
export const localeSlugAliases: Partial<Record<Locale, Record<string, string>>> = {
  en: {
    "aaj-ka-rashifal": "rashifal",
    // /remedies/ 301-redirects to /upay/; hreflang must point to the final URL
    remedies: "upay",
    // Chat-first landing pages whose Hindi twin uses a distinct Hindi-led URL.
    // Keyed by the Hindi slug so language toggle + hreflang resolve from the hi side.
    "jyotishi-se-baat-karen": "talk-to-astrologer",
    "jyotishi-se-chat": "chat-with-astrologer",
    "shaadi-ke-liye-jyotish": "astrologer-for-marriage-problem",
    "career-ke-liye-jyotish": "career-astrology-consultation",
    // Free-chat landing page → distinct Hindi-led URL.
    "muft-jyotish-chat": "free-astrology-chat",
  },
  hi: {
    rashifal: "aaj-ka-rashifal",
    remedies: "upay",
    // Chat-first landing pages: en slug → distinct Hindi-led /hi/<slug>/ URL.
    "talk-to-astrologer": "jyotishi-se-baat-karen",
    "chat-with-astrologer": "jyotishi-se-chat",
    "astrologer-for-marriage-problem": "shaadi-ke-liye-jyotish",
    "career-astrology-consultation": "career-ke-liye-jyotish",
    "free-astrology-chat": "muft-jyotish-chat",
    // /ai-astrologer/ → the existing /hi/astrologers/ consultation twin.
    // Do not alias en.astrologers back to ai-astrologer: /astrologers/ is an
    // indexable page and must self-canonicalize for live indexability guards.
    "ai-astrologer": "astrologers",
  },
};

/**
 * Slugs that have a Hindi equivalent page at /hi/{slug}.
 * Used by LanguageToggle to decide where to link.
 */
export const HINDI_SLUGS = new Set([
  "panchang",
  "rashifal",
  "nakshatra",
  "rashi",
  "free-kundali",
  "kundli-matching",
  "choghadiya",
  "rahu-kaal",
  "muhurat",
  "numerology",
  "name-numerology",
  "mulank-calculator",
  "bhagyank-calculator",
  "baby-names",
  "ascendant-calculator",
  "nakshatra-calculator",
  "rashi-calculator",
  "mangal-dosha-calculator",
  "nadi-astrology",
  "dasha-calculator",
  "gun-milan",
  "sade-sati",
  "transits",
  "remedies",
  "upay",
  "compatibility",
  "astrologers",
  "pandits",
  "talk-to-astrologer",
  "chat-with-astrologer",
  "astrologer-for-marriage-problem",
  "career-astrology-consultation",
  // Chat money pages whose hi twin fixes the LanguageToggle dead-end (rule 46).
  "free-astrology-chat",
  "ai-astrologer",
  // Report sales/landing money pages with Hindi twins at /hi/<slug>/.
  "career-report",
  "marriage-report",
  "dosha-report",
  "dosha-remedy-plan",
  "kaal-sarp-dosha",
  "pitra-dosha",
  "graha",
  "aaj-ka-rashifal",
  "bhai-dooj-2026",
  "chhath-puja-2026",
  "dhanteras-2026",
  "diwali-2026",
  "dussehra-2026",
  "ganesh-chaturthi-2026",
  "karwa-chauth-2026",
  "navratri-2026",
  "raksha-bandhan-2026",
  "holi-2027",
  "janmashtami-2026",
  "govardhan-puja-2026",
  "mantras",
  // Vrat hubs with Hindi twins at /hi/<slug>/ (highest-CVR surfaces, rule 46).
  "ekadashi",
  "amavasya",
  "purnima",
  "pradosh",
  "sankashti-chaturthi",
  "masik-shivratri",
  "sankranti",
  "vrat-calendar",
]);
